import { TRANSITIONS, TRANSITION_MOUNT_TYPE } from '~/enums'
import { secondsToTimelineTime, DEFAULT_VIDEO_HEIGHT, DEFAULT_VIDEO_WIDTH } from '~/Util'
import { ASSET_SIZE_STATUS } from '~/config/constants/asset'
import Asset from './Asset'
import { isCrossingTransition } from '~/helpers/assets/isCrossingTransition'
import { isWipeTransition } from '~/helpers/assets/isWipeTransition'
import { isDipTransition } from '~/helpers/assets/isDipTransition'

/**
 * @typedef {object} MountingValues
 * @property {duration} number
 * @property {startTime} number
 * @property {mediaStart} number
 */

/**
 * @memberOf Assets
 * @extends {Asset}
 */
class TransitionAsset extends Asset {

  static getTranslationKey(type) {
    switch (type) {
      case TRANSITIONS.DISSOLVE:
        return 'TRANSITION_NAME_DISSOLVE'
      case TRANSITIONS.FADEIN:
        return 'TRANSITION_NAME_FADE_IN'
      case TRANSITIONS.FADEOUT:
        return 'TRANSITION_NAME_FADE_OUT'
      case TRANSITIONS.DIP_BLACK:
        return 'TRANSITION_NAME_DIP_TO_BLACK'
      case TRANSITIONS.DIP_WHITE:
        return 'TRANSITION_NAME_DIP_TO_WHITE'
      case TRANSITIONS.BLUR:
        return 'TRANSITION_NAME_BLUR'
      case TRANSITIONS.WIPE_RIGHT:
        return 'TRANSITION_NAME_WIPE_RIGHT'
      case TRANSITIONS.WIPE_LEFT:
        return 'TRANSITION_NAME_WIPE_LEFT'
      case TRANSITIONS.WIPE_UP:
        return 'TRANSITION_NAME_WIPE_UP'
      case TRANSITIONS.WIPE_DOWN:
        return 'TRANSITION_NAME_WIPE_DOWN'
      default:
        return type
    }
  }

  constructor(data = {}) {
    const defaultMountingValues = {
      duration: 0,
      startTime: 0,
      mediaStart: 0,
    }
    const {
      duration = secondsToTimelineTime(3),
      leftVideoAssetId = null,
      rightVideoAssetId = null,
      insertType = ASSET_SIZE_STATUS.NOT_CUT,
      leftAssetMountingValues = { ...defaultMountingValues },
      rightAssetMountingValues = { ...defaultMountingValues },
      type,
      url = null,
      mountType = null,
      ...rest
    } = data

    super({
      duration,
      ...rest,
    })

    this._type = type
    this._leftVideoAssetId = leftVideoAssetId
    this._rightVideoAssetId = rightVideoAssetId
    this._insertType = insertType
    this._leftAssetMountingValues = leftAssetMountingValues
    this._rightAssetMountingValues = rightAssetMountingValues
    this._mountType = mountType
    this._url = url
    this._fileId = null
  }

  // ---

  /**
   * @returns {string|undefined}
   */
  get glslName() {
    switch (this._type) {
      case TRANSITIONS.BLUR:
        return 'GaussianBlur'
      case TRANSITIONS.DISSOLVE:
        return 'displacement'
      case TRANSITIONS.DIP_WHITE:
      case TRANSITIONS.DIP_BLACK:
        return 'dip'
      case TRANSITIONS.FADEIN:
      case TRANSITIONS.FADEOUT:
        return 'fade'
      case TRANSITIONS.WIPE_RIGHT:
        return 'wipeRight'
      case TRANSITIONS.WIPE_LEFT:
        return 'wipeLeft'
      case TRANSITIONS.WIPE_UP:
        return 'wipeUp'
      case TRANSITIONS.WIPE_DOWN:
        return 'wipeDown'
      default:
        return undefined
    }
  }

  // ---

  /**
   * @returns {Object|undefined}
   */
  get glslParams() {
    switch (this._type) {
      case TRANSITIONS.DIP_WHITE:
        return {
          color: [ 1.0, 1.0, 1.0 ],
        }
      case TRANSITIONS.DIP_BLACK:
        return {
          color: [ 0.0, 0.0, 0.0 ],
        }
      case TRANSITIONS.BLUR:
        return {
          resolution: [ DEFAULT_VIDEO_WIDTH, DEFAULT_VIDEO_HEIGHT ],
        }
      default:
        return {}
    }
  }

  // ---

  /**
   * @returns {string|null}
   */
  get url() {
    return this._url
  }

  /**
   * @param {string|null}
   */
  set url(url) {
    this._url = url
  }

  // ---

  /**
   * @returns {MountingValues}
   */
  get leftAssetMountingValues() {
    return this._leftAssetMountingValues
  }

  /**
   * @param {MountingValues} values
   */
  set leftAssetMountingValues(values) {
    this._leftAssetMountingValues = {
      ...this._leftAssetMountingValues,
      ...values,
    }
  }

  /**
   * @returns {MountingValues}
   */
  get rightAssetMountingValues() {
    return this._rightAssetMountingValues
  }

  /**
   * @param {MountingValues} values
   */
  set rightAssetMountingValues(values) {
    this._rightAssetMountingValues = {
      ...this._rightAssetMountingValues,
      ...values,
    }
  }

  // ---

  /**
   * @returns {ASSET_SIZE_STATUS|null}
   */
  get insertType() {
    return this._insertType
  }

  /**
   * @param {ASSET_SIZE_STATUS|null} type
   */
  set insertType(type) {
    this._insertType = type
  }

  // ---

  /**
   * @returns {TRANSITION_MOUNT_TYPE|null}
   */
  get mountType() {
    return this._mountType
  }

  /**
   * @param {TRANSITION_MOUNT_TYPE|null} type
   */
  set mountType(type) {
    this._mountType = type
  }

  // ---

  /**
   * @returns {string}
   */
  get type() {
    return this._type
  }

  set type(type) {
    this._type = type
  }

  // ---

  /*
   * @returns {number|null}
   */
  get leftVideoAssetId() {
    return this._leftVideoAssetId
  }

  set leftVideoAssetId(leftVideoAssetId) {
    this._leftVideoAssetId = leftVideoAssetId
  }

  // ---

  /**
   * @returns {string | number|null}
   */
  get rightVideoAssetId() {
    return this._rightVideoAssetId
  }

  set rightVideoAssetId(rightVideoAssetId) {
    this._rightVideoAssetId = rightVideoAssetId
  }

  // ---

  /**
   * @returns {number}
   */
  get duration() {
    return this._duration
  }

  /**
   * @param {number} time
   */
  set duration(time) {
    this._duration = time
  }

  // ---

  /**
   * @param {number | string} assetId
   * @param {string} side
   * @returns {boolean}
   */
  isAttachedTo(assetId, side = 'any') {
    switch (side) {
      case 'any': return this._rightVideoAssetId === assetId || this._leftVideoAssetId === assetId
      case 'left': return this._leftVideoAssetId === assetId
      case 'right': return this._rightVideoAssetId === assetId
      default: throw new Error('Attached side can be \'any\', \'left\' or \'right\'')
    }
  }

  // ---

  /**
   * @param {number} assetId
   * @returns {boolean}
   */
  isAttachedToLeft(assetId) {
    return this.isAttachedTo(assetId, 'left')
  }

  // ---

  /**
   * @param {number} assetId
   * @returns {boolean}
   */
  isAttachedToRight(assetId) {
    return this.isAttachedTo(assetId, 'right')
  }

  // ---

  /**
   * @param {number} assetId
   * @param {string} side
   */
  attachTo(assetId, side) {
    switch (side) {
      case 'left':
        this._leftVideoAssetId = assetId
        break
      case 'right':
        this._rightVideoAssetId = assetId
        break
      default: throw new Error('Attach side for transition is incorrect. It can be \'left\' or \'right\'')
    }
  }

  // ---

  /**
   * @returns {boolean}
   */
  get isCrossingTransition() {
    return isCrossingTransition(this.type)
  }

  /**
   * @returns {boolean}
   */
  get isWipeTransition() {
    return isWipeTransition(this.type)
  }

  /**
   * @returns {boolean}
   */
  get isDipTransition() {
    return isDipTransition(this.type)
  }

  /**
   * @returns {boolean}
   */
  get isMountIn() {
    return this.mountType === TRANSITION_MOUNT_TYPE.IN
  }

  /**
   * @returns {boolean}
   */
  get isMountOut() {
    return this.mountType === TRANSITION_MOUNT_TYPE.OUT
  }

  /**
   * @returns {boolean}
   */
  get isMountBetween() {
    return this.mountType === TRANSITION_MOUNT_TYPE.BETWEEN
  }

  // ---

  /**
   * @returns {Asset[]}
  */
  relatedAssets(assets) {
    return assets.filter(asset => asset.id === this._leftVideoAssetId
      || asset.id === this._rightVideoAssetId)
  }

}

export default TransitionAsset
